import {
  Box,
  Checkbox,
  FormControlLabel,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { TimePicker } from '@mui/x-date-pickers';

interface TimeRangeSelectorProps {
  day: string;
  isEnabled: boolean;
  isAllDay: boolean;
  startTime: dayjs.Dayjs | null;
  endTime: dayjs.Dayjs | null;
  error?: {
    start?: boolean;
    end?: boolean;
  };
  helperText?: string;
  onEnabledChange: (enabled: boolean) => void;
  onAllDayChange: (allDay: boolean) => void;
  onStartTimeChange: (time: dayjs.Dayjs | null) => void;
  onEndTimeChange: (time: dayjs.Dayjs | null) => void;
  onStartTimeBlur?: () => void;
  onEndTimeBlur?: () => void;
}

export default function TimeRangeSelector({
  day,
  isEnabled,
  isAllDay,
  startTime,
  endTime,
  error,
  helperText,
  onEnabledChange,
  onAllDayChange,
  onStartTimeChange,
  onEndTimeChange,
  onStartTimeBlur,
  onEndTimeBlur,
}: TimeRangeSelectorProps) {
  const { t } = useTranslation();

  const handleTimeChange = (
    type: 'start' | 'end',
    newTime: dayjs.Dayjs | null
  ) => {
    if (!newTime || !newTime.isValid()) {
      onEndTimeChange(null);
      return;
    }

    // Create a new dayjs object with today's date and the selected time
    const time = dayjs()
      .hour(newTime.hour())
      .minute(newTime.minute())
      .second(0);
    if (type === 'start') {
      onStartTimeChange(time);
    } else {
      onEndTimeChange(time);
    }
  };

  return (
    <Box
      sx={{ p: 2, borderRadius: 1, bgcolor: 'background.paper', boxShadow: 1 }}
    >
      <Stack spacing={2}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="subtitle1" fontWeight="bold">
            {day}
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={isEnabled}
                onChange={(e) => onEnabledChange(e.target.checked)}
              />
            }
            label={t('common.enabled')}
          />
        </Stack>

        {isEnabled && (
          <>
            <ToggleButtonGroup
              value={isAllDay ? 'allDay' : 'custom'}
              exclusive
              onChange={(_, value) => onAllDayChange(value === 'allDay')}
              fullWidth
              size="small"
            >
              <ToggleButton value="allDay">
                {t('menu.availability.allDay')}
              </ToggleButton>
              <ToggleButton value="custom">
                {t('menu.availability.customTime')}
              </ToggleButton>
            </ToggleButtonGroup>

            {!isAllDay && (
              <Stack direction="row" spacing={2}>
                <TimePicker
                  label={t('menu.availability.startTime')}
                  value={startTime}
                  onChange={(time) => handleTimeChange('start', time)}
                  sx={{ flex: 1 }}
                  format="HH:mm"
                  ampm={false}
                  slotProps={{
                    textField: {
                      error: error?.start,
                      helperText: error?.start ? helperText : undefined,
                      onBlur: onStartTimeBlur,
                    },
                  }}
                />
                <TimePicker
                  label={t('menu.availability.endTime')}
                  value={endTime}
                  onChange={(time) => handleTimeChange('end', time)}
                  sx={{ flex: 1 }}
                  format="HH:mm"
                  ampm={false}
                  slotProps={{
                    textField: {
                      error: error?.end,
                      helperText: error?.end ? helperText : undefined,
                      onBlur: onEndTimeBlur,
                    },
                  }}
                />
              </Stack>
            )}
          </>
        )}
      </Stack>
    </Box>
  );
}
